import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getPersonalityResult, getPersonalityQuestions, submitPersonality
} from '../lib/api';
import { SKETCH_PATTERNS } from '../lib/images';
import { Loader2, ChevronRight, Sparkles, Brain, ArrowRight, Zap, Heart } from 'lucide-react';
import PersonalityOnboarding from '../components/personality/PersonalityOnboarding';
import PersonalityQuestions from '../components/personality/PersonalityQuestions';
import PersonalityResult from '../components/personality/PersonalityResult';
import IQTestPage from './IQTestPage';

export default function PersonalityPage({ initialTab }) {
  const location = useLocation();
  const navigate = useNavigate();
  // URL is source of truth: /personality/iq → iq, /personality → eq
  const activeTab = initialTab === 'iq' || location.pathname.endsWith('/iq') ? 'iq' : 'eq';

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="personality-page">
      {/* Tab Selector */}
      <div className="flex gap-3 mb-6 sm:mb-8">
        <button onClick={() => navigate('/personality')} data-testid="personality-tab-eq"
          className="flex-1 sm:flex-none px-5 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
          style={{
            background: activeTab === 'eq' ? 'var(--primary)' : 'var(--surface)',
            color: activeTab === 'eq' ? 'var(--primary-fg)' : 'var(--text)',
            border: `1px solid ${activeTab === 'eq' ? 'var(--primary)' : 'var(--border)'}`,
          }}>
          <Heart className="w-4 h-4" /> Emotional Quotient
        </button>
        <button onClick={() => navigate('/personality/iq')} data-testid="personality-tab-iq"
          className="flex-1 sm:flex-none px-5 py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
          style={{
            background: activeTab === 'iq' ? '#3B82F6' : 'var(--surface)',
            color: activeTab === 'iq' ? 'white' : 'var(--text)',
            border: `1px solid ${activeTab === 'iq' ? '#3B82F6' : 'var(--border)'}`,
          }}>
          <Zap className="w-4 h-4" /> Intelligence Quotient
        </button>
      </div>

      {/* EQ Tab */}
      {activeTab === 'eq' && <EQTest />}

      {/* IQ Tab */}
      {activeTab === 'iq' && <IQTestPage />}
    </div>
  );
}

// ─── Emotional Quotient Test (existing) ───
function EQTest() {
  const { deviceId } = useAuth();
  const [step, setStep] = useState('loading');
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [occupation, setOccupation] = useState('');
  const [loading, setLoading] = useState(true);

  const checkExistingResult = useCallback(async () => {
    try {
      const res = await getPersonalityResult(deviceId);
      if (res.data?.has_result) {
        setResult(res.data);
        setStep('result');
      } else {
        setStep('intro');
      }
    } catch {
      setStep('intro');
    }
    setLoading(false);
  }, [deviceId]);

  useEffect(() => { checkExistingResult(); }, [checkExistingResult]);

  const handleOnboardingComplete = async (data) => {
    setOccupation(data.occupation);
    try {
      const res = await getPersonalityQuestions(deviceId, data.occupation);
      setQuestions(res.data?.questions || []);
      setStep('questions');
    } catch {
      setStep('intro');
    }
  };

  const handleSubmitAnswers = async (answers) => {
    try {
      const res = await submitPersonality({ device_id: deviceId, answers });
      setResult(res.data);
      setStep('result');
    } catch {}
  };

  const handleRetake = () => {
    setResult(null);
    setQuestions([]);
    setStep('intro');
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <>
      {step === 'intro' && <EQIntro onStart={() => setStep('onboarding')} />}
      {step === 'onboarding' && (
        <PersonalityOnboarding deviceId={deviceId} onComplete={handleOnboardingComplete} onBack={() => setStep('intro')} />
      )}
      {step === 'questions' && (
        <PersonalityQuestions questions={questions} onSubmit={handleSubmitAnswers} onBack={() => setStep('onboarding')} />
      )}
      {step === 'result' && (
        <PersonalityResult result={result} onRetake={handleRetake} />
      )}
    </>
  );
}

function EQIntro({ onStart }) {
  return (
    <div className="animate-fade-in max-w-2xl mx-auto text-center">
      <div className="rounded-2xl overflow-hidden mb-8 relative py-16 px-8 keep-light-theme"
        style={{ background: 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '250px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div className="relative z-10">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
            <Brain className="w-10 h-10" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Discover Your Wellness Personality
          </h1>
          <p className="text-sm leading-relaxed max-w-md mx-auto" style={{ color: 'var(--muted-fg)' }}>
            Answer 10 simple questions to uncover your unique wellness archetype and get personalized insights.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: '5 min', title: 'Quick & Fun', desc: 'Takes just 5 minutes' },
          { icon: '10', title: '10 Questions', desc: 'Thoughtfully crafted' },
          { icon: Sparkles, title: 'Personalized', desc: 'Unique to you' },
        ].map((item, i) => (
          <div key={i} className={`rounded-lg p-5 border animate-fade-in stagger-${i + 1}`}
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            {typeof item.icon === 'string' ? (
              <p className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--primary)' }}>{item.icon}</p>
            ) : (
              <item.icon className="w-6 h-6 mb-2" style={{ color: 'var(--primary)' }} />
            )}
            <p className="font-bold text-sm mb-0.5" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{item.title}</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{item.desc}</p>
          </div>
        ))}
      </div>

      <button onClick={onStart} data-testid="start-personality-test"
        className="px-8 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] inline-flex items-center gap-2"
        style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
        Start the Test <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}
